"use client"

import { ApiData } from "../InvestmentSimulator/types"

export interface CompanyGrowthData {
  id: number
  name: string
  percentage: number
  src?: string
  alt?: string
}

/**
 * Calculates the 5-year growth percentages for each company
 * @param apiData API data containing company investment information
 * @returns Array of company growth data with percentages
 */
export const calculateCompanyGrowthPercentages = (
  apiData?: ApiData,
): CompanyGrowthData[] => {
  if (!apiData || !apiData.companies || !apiData.companiesInvestmentData) {
    return []
  }

  return apiData.companies.map((company) => {
    // Find company investment data
    const companyInvestment = apiData.companiesInvestmentData.find(
      (data) => data.companyId === company.id,
    )

    if (
      !companyInvestment ||
      !companyInvestment.fundsData ||
      companyInvestment.fundsData.length === 0
    ) {
      return {
        id: company.id,
        name: company.name,
        percentage: 0,
        src: "", // Should be provided by itemsList
        alt: company.name,
      }
    }

    // Find the fund with name "מניות" instead of using the first fund
    const stockFundId = apiData.funds.find((fund) => fund.name === "מניות")?.id

    // If stockFundId is found, use that fund, otherwise fallback to first fund
    const fundData = stockFundId
      ? companyInvestment.fundsData.find((fd) => fd.fundId === stockFundId)
      : companyInvestment.fundsData[0]

    // If the specific fund wasn't found, fallback to the first fund
    const fundToUse = fundData || companyInvestment.fundsData[0]

    if (!fundToUse.dataList || fundToUse.dataList.length < 2) {
      return {
        id: company.id,
        name: company.name,
        percentage: 0,
        src: "",
        alt: company.name,
      }
    }

    // Process the data points
    const allData = fundToUse.dataList.map((item) => {
      const dateObj = new Date(item.date)
      const value = item.value !== undefined ? item.value : 0
      return { dateObj, value }
    })

    // Sort by date to ensure correct order
    allData.sort((a, b) => a.dateObj.getTime() - b.dateObj.getTime())

    // Filter for 5-year timeframe
    const latestDate = allData[allData.length - 1].dateObj
    const periodStart = new Date(latestDate)
    periodStart.setFullYear(periodStart.getFullYear() - 5)

    const periodData = allData.filter((item) => item.dateObj >= periodStart)
    const dataToUse = periodData.length > 0 ? periodData : allData

    // Get first and last values for calculation
    const initialValue = dataToUse[0].value
    const finalValue = dataToUse[dataToUse.length - 1].value

    // Calculate growth percentage
    let growthPercentage = 0
    if (initialValue > 0) {
      growthPercentage = ((finalValue - initialValue) / initialValue) * 100
    }

    return {
      id: company.id,
      name: company.name,
      percentage: Math.round(growthPercentage), // Round to nearest integer
      src: "", // Should be provided by itemsList
      alt: company.name,
    }
  })
}

/**
 * Merges growth data with item list to get company logos
 * @param growthData Array of company growth data
 * @param itemsList List of items with logo information
 * @returns Merged company growth data with logos
 */
export const mergeGrowthDataWithItems = (
  growthData: CompanyGrowthData[],
  itemsList: any[],
): CompanyGrowthData[] => {
  return growthData.map((growth) => {
    const item = itemsList.find((item) => item.id === growth.id) || {}
    return {
      ...growth,
      src: item.src || "",
      alt: item.alt || growth.name,
      percentage: growth.percentage,
    }
  })
}

/**
 * Gets the growth percentage for a specific company
 * @param apiData API data containing company investment information
 * @param companyName The name of the company to get percentage for
 * @returns The growth percentage or null if not found
 */
export const getCompanyGrowthPercentage = (
  apiData?: ApiData,
  companyName?: string,
): number | null => {
  if (!apiData || !companyName) {
    return null
  }

  // Calculate percentages for all companies
  const growthData = calculateCompanyGrowthPercentages(apiData)

  // Find the specific company by name
  const company = growthData.find(
    (company) =>
      company.name === companyName ||
      company.name.includes(companyName) ||
      companyName.includes(company.name),
  )

  return company ? company.percentage : null
}

export const matchItemsWithGrowthData = (
  growthData: CompanyGrowthData[],
  itemsList: any[],
): any[] => {
  return itemsList.map((item) => {
    // Find matching company in growthData by name
    // Check if item.title contains the company name or vice versa
    const matchedCompany = growthData.find(
      (company) =>
        item.title?.includes(company.name) ||
        company.name?.includes(item.title),
    )

    // Return new object with all item properties plus percentage
    return {
      ...item,
      percentage: matchedCompany?.percentage || 0,
      src: item.logo?.src || "",
      alt: item.logo?.alt || "",
    }
  })
}
